import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/firebaseConfig";
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged
} from "firebase/auth";
import { setDoc, doc, getDoc } from "firebase/firestore";
import captainsImage from "../logos/Captions.jpg";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [inAppBrowserWarning, setInAppBrowserWarning] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    // Detect in-app browsers like WhatsApp, Instagram, Facebook
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (
      ua.indexOf("FBAN") > -1 ||
      ua.indexOf("FBAV") > -1 ||
      ua.indexOf("Instagram") > -1 ||
      ua.indexOf("WhatsApp") > -1 ||
      ua.indexOf("Threads") > -1
    ) {
      setInAppBrowserWarning(true);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 20000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        localStorage.setItem("username", user.displayName || user.email.split('@')[0]);
        localStorage.setItem("uid", user.uid);
        if (user.email === "parthadmin@gmail.com") {
          navigate("/admin");
        } else {
          navigate("/home");
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const syncUserProfile = async (user) => {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // New user, initialize points
      await setDoc(userRef, {
        uid: user.uid,
        displayName: user.displayName || user.email.split('@')[0],
        email: user.email,
        points: 100, // Welcome bonus
        totalPredictions: 0,
        correctPredictions: 0,
        predictions: []
      });
    }

    localStorage.setItem("username", user.displayName || user.email.split('@')[0]);
    localStorage.setItem("uid", user.uid);

    if (user.email === "parthadmin@gmail.com") {
      navigate("/admin");
    } else {
      navigate("/home");
    }
  };

  const handleGoogleSignIn = async () => {
    if (inAppBrowserWarning) {
      setError("Google Sign-In is blocked by this app's browser (e.g. WhatsApp/Instagram). Please tap the menu icon (⋮) and select 'Open in Browser' (Chrome/Safari), or use Email/Password below.");
      return;
    }
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await syncUserProfile(result.user);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEmailSignIn = async (isSignUp) => {
    try {
      let result;
      if (isSignUp) {
        result = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        result = await signInWithEmailAndPassword(auth, email, password);
      }
      await syncUserProfile(result.user);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      {showSplash && (
        <div className="splash-screen">
          <div className="splash-ball"></div>
          <div className="stumps-container">
            <div className="stump"></div>
            <div className="stump"></div>
            <div className="stump"></div>
          </div>
          <div className="splash-text">
            {"Step Into the Arena of Champions".split(" ").map((word, index) => (
              <span key={index} className="splash-word" style={{ animationDelay: `${index * 0.4}s` }}>
                {word}
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={styles.container}>
        <div className="glass-morphism animate-fade-in" style={styles.card}>
          <h1 className="gradient-text" style={styles.title}>IPL ARENA</h1>
          <p style={styles.subtitle}>Authentication & Synchronization Ready</p>

          {inAppBrowserWarning && (
            <div style={styles.warningBox}>
              <strong>⚠️ In-App Browser Detected</strong><br />
              Google Sign-In won't work inside WhatsApp/Instagram due to security restrictions. <br /><br />
              <strong>Fix:</strong> Tap the top right menu (⋮) and choose <strong>"Open in Browser"</strong> (Chrome/Safari), or simply create an account with <strong>Email & Password</strong> below.
            </div>
          )}

          <div style={styles.authSection}>
            <button
              onClick={handleGoogleSignIn}
              style={{ ...styles.googleBtn, opacity: inAppBrowserWarning ? 0.6 : 1 }}
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" style={styles.googleIcon} />
              Continue with Google
            </button>

            <div style={styles.divider}>
              <div style={styles.line}></div>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>OR</span>
              <div style={styles.line}></div>
            </div>

            <div style={styles.inputWrapper}>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
              />
            </div>

            {error && <p style={styles.errorText}>{error}</p>}

            <div style={styles.buttonGroup}>
              <button onClick={() => handleEmailSignIn(false)} style={styles.signInBtn}>Sign In</button>
              <button onClick={() => handleEmailSignIn(true)} style={styles.signUpBtn}>Sign Up</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  container: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: `radial-gradient(circle at top right, rgba(30, 41, 59, 0.85), rgba(7, 11, 20, 0.95)), url(${captainsImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '2rem',
  },
  card: {
    padding: "3rem",
    borderRadius: "24px",
    textAlign: "center",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: '900',
    marginBottom: '0.5rem',
  },
  subtitle: {
    color: "var(--text-secondary)",
    fontSize: "0.9rem",
    marginBottom: "2.5rem",
  },
  warningBox: {
    background: "rgba(220, 38, 38, 0.15)",
    border: "1px solid rgba(220, 38, 38, 0.5)",
    color: "#fca5a5",
    padding: "1rem",
    borderRadius: "12px",
    marginBottom: "1.5rem",
    fontSize: "0.85rem",
    textAlign: "left",
    lineHeight: "1.4",
  },
  authSection: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  googleBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
    width: "100%",
    padding: "0.8rem",
    borderRadius: "12px",
    background: "white",
    color: "#333",
    fontWeight: "600",
    border: "none",
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
  },
  googleIcon: {
    width: "20px",
    height: "20px",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    margin: "0.5rem 0",
  },
  line: {
    flex: 1,
    height: "1px",
    background: "var(--glass-border)",
  },
  inputWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  input: {
    width: "100%",
    padding: "0.8rem",
    borderRadius: "10px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--glass-border)",
    color: "white",
    fontSize: "0.9rem",
    outline: "none",
  },
  buttonGroup: {
    display: "flex",
    gap: "0.75rem",
  },
  signInBtn: {
    flex: 1,
    padding: "0.8rem",
    borderRadius: "10px",
    border: "none",
    background: "var(--primary)",
    color: "black",
    fontWeight: "700",
  },
  signUpBtn: {
    flex: 1,
    padding: "0.8rem",
    borderRadius: "10px",
    border: "1px solid var(--primary)",
    background: "transparent",
    color: "var(--primary)",
    fontWeight: "700",
  },
  errorText: {
    color: "#ef4444",
    fontSize: "0.8rem",
    marginTop: "0.5rem",
  }
};

export default Login;
